<script setup lang="ts">
import { scan, reverse } from "ramda";

const sn = ref("")

// async function checkSN(theSN: string) {
//   const f = await fetch(
//     `https://www.stata.com/updates1801/up.cgi?s=${theSN}&v=1800&r=1`, {
//     mode: 'no-cors'
//   });
//   console.log(`This SN (${theSN}) is ${await f.text()}`);
// }

const user = ref(1);

const license = ref("a")
const licenses = new Map([
  ["a", "Basic License"],
  ["c", "Network License"],
  ["g", "Student Lab License"],
  ["e", "Compute Server License"]
])

const edition = ref("be")
const editions = new Map([
  ["be", "Stata/BE"],
  ["se", "Stata/SE"],
  ["mp", "Stata/MP"]
])

const perpetual = ref(false);
const core = ref(1);

const expire = ref("")

function encode(info: string) {
  const base37 = {
    map: "0123456789abcdefghijklmnopqrstuvwxyz$",
    decode(og: string): number[] {
      const result = [];
      for (let i = 0; i < og.length; i++) {
        result.push(this.map.indexOf(og[i]));
      }
      return result;
    },
    encode(og: number[]): string {
      let result = "";
      for (let i = 0; i < og.length; i++) {
        result += this.map[og[i]];
      }
      return result;
    }
  }
  function overflowAdd(a: number, b: number) {
    const c = a + b;
    return c >= 37 ? (c - 37) : c;
  }

  const replaced = base37.decode(info)
  // sum of all digits, digits on even positions, digits on odd positions
  const sum = replaced.reduce((a, b) => a + b, 0) % 37;
  const even = replaced.filter((_, i) => i % 2 == 0).reduce((a, b) => a + b, 0) % 37;
  const odd = replaced.filter((_, i) => i % 2 == 1).reduce((a, b) => a + b, 0) % 37;

  const checksummed = [...replaced, sum, odd, even];

  const [_, ...convolted1] = scan(overflowAdd, 0, checksummed);
  const [__, ...convolted2Rev] = scan(overflowAdd, 0, reverse(convolted1));
  const convolted2 = reverse(convolted2Rev);

  const code = base37.encode(convolted2.slice(4));
  const auth = base37.encode(convolted2.slice(0, 4));

  return { code, auth };
}

const key: Ref<{ sn: string, code: string, auth: string, } | undefined> = ref()

function keygen() {
  const realSN = (sn.value == "" ? `${Math.floor(Math.random() * 10000000000)}` : sn.value);
  const info = realSN + `$${999}` + `$24`
    + `$${edition.value == "be" ? "2" : "5"}`
    + `$${user.value}$${license.value}`
    + `$${perpetual.value ? "" : expire.value.replaceAll("/", "")}`
    + (edition.value == "mp" ? `$${core.value}` : "");
  const { code, auth } = encode(info);
  key.value = { sn: realSN, code, auth };
}
</script>

<template>
  <div>
    <cv-form @submit.prevent="() => keygen()" class="mb-1rem">
      <cv-text-input v-model="sn" label="Serial Number" helperText="\d{1,20}"
        placeholder="Leave it empty for a random number" />
      <cv-number-input v-model="user" :label="license != 'g' ? 'User' : 'Student'" min="1" max="9999" />
      <cv-dropdown v-model="license" label="License">
        <cv-dropdown-item v-for="[k, v] in licenses" :value="k">{{ v }}</cv-dropdown-item>
      </cv-dropdown>
      <cv-dropdown v-model="edition" label="Edition">
        <cv-dropdown-item v-for="[k, v] in editions" :value="k">{{ v }}</cv-dropdown-item>
      </cv-dropdown>
      <cv-toggle v-model="perpetual" label="License Term">
        <template v-slot:text-left> Finite </template>
        <template v-slot:text-right> Prepetual </template>
      </cv-toggle>
      <cv-date-picker v-model="expire" v-if="!perpetual" dateLabel="Expire Date" kind="single" />
      <cv-slider v-model="core" v-if="edition == 'mp'" label="Core" min="2" max="64" />
      <cv-button type="submit">Generate</cv-button>
    </cv-form>
    <p v-if="key">
      Your SN: <cv-code-snippet kind="inline">
        {{ key.sn }}
      </cv-code-snippet>
      <br />
      Your Code: <cv-code-snippet kind="inline">
        {{ key.code }}
      </cv-code-snippet>
      <br />
      Your Auth: <cv-code-snippet kind="inline">
        {{ key.auth }}
      </cv-code-snippet>
    </p>
  </div>
</template>

<style lang="scss">
.bx--form-item {
  margin-bottom: 1rem;
}
</style>